import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiBell, FiChevronDown, FiChevronRight, FiSettings, FiLogOut, FiClock, FiAlertCircle } from 'react-icons/fi';
import { MdDashboard, MdCalendarToday, MdFormatListBulleted, MdStar, MdSettings, MdLogout, MdInsights } from 'react-icons/md';
import zibitLogo from '../images/zibit-dark-logo.png';
import './DashboardLayout.css';

function DashboardLayout({ children }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [eventsOpen, setEventsOpen] = useState(
    location.pathname.startsWith('/events') ||
    location.pathname.startsWith('/playbook') ||
    location.pathname.startsWith('/project-overview')
  );
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const [notifTasks, setNotifTasks] = useState([]);
  const [profile, setProfile] = useState(null);
  // The sidebar lists the user's shows so each can expand to its own
  // playbook, budget, review and forecast.
  const [sidebarEvents, setSidebarEvents] = useState([]);
  // Keep the tree open across navigation: without this, clicking Budget
  // collapses the show you just expanded.
  const [eventsVersion, setEventsVersion] = useState(0);
  const [openEventId, setOpenEventId] = useState(
    new URLSearchParams(location.search).get('eventId')
  );

  useEffect(() => {
    const current = new URLSearchParams(location.search).get('eventId');
    if (current) setOpenEventId(current);
  }, [location.search]);

  // TODO: when other components need shared task state, lift this to a TasksContext provider.
  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
  useEffect(() => {
    if (!user.userId) return;
    fetch(`${API_BASE_URL}/tasks?userId=${user.userId}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const today = new Date(); today.setHours(0,0,0,0);
        const endOfWeek = new Date(today); endOfWeek.setDate(today.getDate() + 7);
        const filtered = (data || []).filter(t => {
          if (!t.dueDate || t.status === 'completed') return false;
          const due = new Date(t.dueDate);
          return due <= endOfWeek;
        });
        filtered.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        setNotifTasks(filtered);
      })
      .catch(() => setNotifTasks([]));
    fetch(`${API_BASE_URL}/events?userId=${user.userId}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setSidebarEvents(Array.isArray(data) ? data : []))
      .catch(() => setSidebarEvents([]));
    fetch(`${API_BASE_URL}/user/profile?userId=${user.userId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setProfile(data); })
      .catch(() => {});
    // Refetch on navigation: a show published or renamed on the Events page
    // would otherwise not appear here until the next full page load.
  }, [user.userId, API_BASE_URL, location.pathname, eventsVersion]);

  // Events.js raises this after a save or delete so the sidebar updates
  // without the user having to navigate away and back.
  useEffect(() => {
    function onEventsChanged() { setEventsVersion(v => v + 1); }
    window.addEventListener('zibit:events-changed', onEventsChanged);
    return () => window.removeEventListener('zibit:events-changed', onEventsChanged);
  }, []);

  function getNotifCategory(dueDate) {
    const today = new Date(); today.setHours(0,0,0,0);
    const due = new Date(dueDate); due.setHours(0,0,0,0);
    if (due < today) return 'overdue';
    if (due.getTime() === today.getTime()) return 'today';
    return 'soon';
  }

  function formatNotifDate(dueDate) {
    const today = new Date(); today.setHours(0,0,0,0);
    const due = new Date(dueDate); due.setHours(0,0,0,0);
    const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  }

  const overdueCount = notifTasks.filter(t => getNotifCategory(t.dueDate) === 'overdue').length;
  const todayCount = notifTasks.filter(t => getNotifCategory(t.dueDate) === 'today').length;
  const badgeCount = overdueCount + todayCount;

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    function handleClickOutsideNotif(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('mousedown', handleClickOutsideNotif);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutsideNotif);
    };
  }, []);

  function handleLogout() {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  }

  const isActive = (path) => location.pathname === path;
  // Only published shows appear in the sidebar; drafts would clutter it and
  // completed shows remain reachable from All Events. Stored as 'active',
  // shown as 'Published' in the events form.
  const publishedEvents = sidebarEvents.filter(
    e => (e.status || '').toLowerCase() === 'active'
  );
  const isEventsSection = location.pathname.startsWith('/events');
  // A per-show link is only current when both the page and the show match,
  // otherwise every show's Budget would highlight at once.
  const currentEventId = new URLSearchParams(location.search).get('eventId');
  const isEventLinkActive = (path, eventId) =>
    location.pathname === path && currentEventId === eventId;

  // Display name + initials.
  // Prefer the freshly-fetched profile (always current); fall back to the
  // localStorage user (set at login, which omits photo/username) until it loads.
  const effectiveUser = profile || user;
  const displayName = [effectiveUser.firstName, effectiveUser.lastName].filter(Boolean).join(' ') || effectiveUser.email || 'User';
  const initials = (
    (effectiveUser.firstName?.[0] || '') + (effectiveUser.lastName?.[0] || '')
  ).toUpperCase() || (effectiveUser.email?.[0] || 'U').toUpperCase();
  const rawPhotoPath = effectiveUser.profilePicture || effectiveUser.profile_picture || null;
  const photoUrl = rawPhotoPath
    ? (rawPhotoPath.startsWith('http') ? rawPhotoPath : `${API_BASE_URL}/upload?file=${encodeURIComponent(rawPhotoPath)}`)
    : null;

  return (
    <div className="dashboard-layout">
      <div className={`sidebar ${sidebarExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-header">
          <div className="logo-section">
            <img src={zibitLogo} alt="Zibit" className="sidebar-logo" />
          </div>
          <button
            className="collapse-btn"
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
          >
            <FiChevronRight size={14} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <Link to="/dashboard" className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}>
            <MdDashboard className="nav-icon" />
            {sidebarExpanded && <span className="nav-label">Dashboard</span>}
          </Link>
          <Link to="/calendar" className={`nav-item ${isActive('/calendar') ? 'active' : ''}`}>
            <MdCalendarToday className="nav-icon" />
            {sidebarExpanded && <span className="nav-label">Calendar</span>}
          </Link>
          <Link to="/tasks" className={`nav-item ${isActive('/tasks') ? 'active' : ''}`}>
            <MdFormatListBulleted className="nav-icon" />
            {sidebarExpanded && <span className="nav-label">Tasks</span>}
          </Link>
          <div className="nav-group">
            <button
              className={`nav-item nav-toggle ${isEventsSection ? 'active' : ''}`}
              onClick={() => setEventsOpen(!eventsOpen)}
            >
              <MdStar className="nav-icon" />
              {sidebarExpanded && <span className="nav-label">Events</span>}
              {sidebarExpanded && (
                <FiChevronDown
                  className={`nav-arrow ${eventsOpen ? 'open' : ''}`}
                  size={14}
                />
              )}
            </button>
            {sidebarExpanded && eventsOpen && (
              <div className="nav-sub-items">
                <Link to="/events" className={`nav-sub-item ${isActive('/events') ? 'active' : ''}`}>
                  All Events
                </Link>
                <div className="nav-group-label">Published Shows</div>
                {publishedEvents.length === 0 && (
                  <div className="nav-group-empty">Publish a show to see it here</div>
                )}
                {publishedEvents.map(ev => (
                  <div key={ev.id} className="nav-event-group">
                    <button
                      className={`nav-sub-item nav-event-toggle ${openEventId === ev.id ? 'active' : ''}`}
                      onClick={() => setOpenEventId(openEventId === ev.id ? null : ev.id)}
                      title={ev.eventName}
                    >
                      <span className="nav-event-name">{ev.eventName}</span>
                      <FiChevronDown
                        className={`nav-arrow ${openEventId === ev.id ? 'open' : ''}`}
                        size={12}
                      />
                    </button>
                    {openEventId === ev.id && (
                      <div className="nav-event-links">
                        <Link
                          to={`/playbook?eventId=${ev.id}`}
                          className={`nav-event-link ${isEventLinkActive('/playbook', ev.id) ? 'active' : ''}`}
                        >Playbook</Link>
                        <Link
                          to={`/events/budget?eventId=${ev.id}`}
                          className={`nav-event-link ${isEventLinkActive('/events/budget', ev.id) ? 'active' : ''}`}
                        >Budget</Link>
                        <Link
                          to={`/project-overview?eventId=${ev.id}`}
                          className={`nav-event-link ${isEventLinkActive('/project-overview', ev.id) ? 'active' : ''}`}
                        >Show Review</Link>
                        <Link
                          to={`/events/forecast?eventId=${ev.id}`}
                          className={`nav-event-link ${isEventLinkActive('/events/forecast', ev.id) ? 'active' : ''}`}
                        >Forecast</Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Link to="/settings" className={`nav-item ${isActive('/settings') ? 'active' : ''}`}>
            <MdSettings className="nav-icon" />
            {sidebarExpanded && <span className="nav-label">Settings</span>}
          </Link>
          <button className="nav-item" onClick={handleLogout}>
            <MdLogout className="nav-icon" />
            {sidebarExpanded && <span className="nav-label">Logout</span>}
          </button>
        </nav>
      </div>

      <div className={`main-content ${sidebarExpanded ? '' : 'sidebar-collapsed'}`}>
        <div className="topbar">
          <div className="topbar-left"></div>
          <div className="topbar-right">
            <div className="topbar-notif-wrapper" ref={notifRef}>
              <button
                className="topbar-icon-btn"
                aria-label="Notifications"
                onClick={() => setNotifOpen(!notifOpen)}
              >
                <FiBell size={20} />
                {badgeCount > 0 && (
                  <span className="topbar-notif-badge">{badgeCount > 9 ? '9+' : badgeCount}</span>
                )}
              </button>
              {notifOpen && (
                <div className="notif-menu" role="menu">
                  <div className="notif-menu-header">
                    <span>Notifications</span>
                    {notifTasks.length > 0 && <span className="notif-menu-count">{notifTasks.length}</span>}
                  </div>
                  <div className="notif-menu-body">
                    {notifTasks.length === 0 ? (
                      <div className="notif-empty">
                        <FiClock size={28} />
                        <p>You're all caught up</p>
                        <span>No tasks due in the next 7 days.</span>
                      </div>
                    ) : (
                      notifTasks.map((t, i) => {
                        const cat = getNotifCategory(t.dueDate);
                        return (
                          <Link
                            to="/tasks"
                            key={i}
                            className={`notif-item notif-${cat}`}
                            onClick={() => setNotifOpen(false)}
                          >
                            <div className="notif-item-icon">
                              {cat === 'overdue' ? <FiAlertCircle size={16} /> : <FiClock size={16} />}
                            </div>
                            <div className="notif-item-body">
                              <div className="notif-item-title">{t.taskName}</div>
                              <div className="notif-item-meta">
                                {t.eventName && <span>{t.eventName} · </span>}
                                {formatNotifDate(t.dueDate)}
                              </div>
                            </div>
                          </Link>
                        );
                      })
                    )}
                  </div>
                  {notifTasks.length > 0 && (
                    <Link
                      to="/tasks"
                      className="notif-menu-footer"
                      onClick={() => setNotifOpen(false)}
                    >
                      View all tasks
                    </Link>
                  )}
                </div>
              )}
            </div>

            <div className="topbar-user-wrapper" ref={userMenuRef}>
              <button
                className="topbar-user"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <span className="user-name">{displayName}</span>
                <div className="user-avatar-wrapper">
                  {photoUrl ? (
                    <img src={photoUrl} alt={displayName} className="user-avatar" />
                  ) : (
                    <span className="user-avatar-initials">{initials}</span>
                  )}
                </div>
                <FiChevronDown
                  size={14}
                  className={`user-dropdown ${userMenuOpen ? 'open' : ''}`}
                />
              </button>

              {userMenuOpen && (
                <div className="user-menu" role="menu">
                  <div className="user-menu-header">
                    <div className="user-avatar-wrapper user-menu-avatar">
                      {photoUrl ? (
                        <img src={photoUrl} alt={displayName} className="user-avatar" />
                      ) : (
                        <span className="user-avatar-initials">{initials}</span>
                      )}
                    </div>
                    <div className="user-menu-info">
                      <div className="user-menu-name">{displayName}</div>
                      <div className="user-menu-email">{user.email || ''}</div>
                    </div>
                  </div>
                  <div className="user-menu-divider"></div>
                  <Link
                    to="/settings"
                    className="user-menu-item"
                    onClick={() => setUserMenuOpen(false)}
                    role="menuitem"
                  >
                    <FiSettings size={16} /> Settings
                  </Link>
                  <div className="user-menu-divider"></div>
                  <button
                    className="user-menu-item user-menu-logout"
                    onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                    role="menuitem"
                  >
                    <FiLogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="page-content">
          {children}
        </div>
        <footer className="dashboard-footer">
          <p>© 2026 Zibit LLC</p>
        </footer>
      </div>
    </div>
  );
}

export default DashboardLayout;
